import * as React from "react";
import {useEffect} from "react";
import {CSSObject, styled, Theme} from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, {AppBarProps as MuiAppBarProps} from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import logo from "../../../assets/images/logo-nrecontrol.svg";
import logoNpi from "../../../assets/images/logo-npi.png";
import logoFoxconn from "../../../assets/images/logo-footer.svg";
import logoFPFTech from "../../../assets/images/logo-fpf.svg";
import { MdViewModule } from "react-icons/md";
import {
    Badge,
    Box,
    Button,
    Container,
    Grid,
    ListItem,
    ListSubheader,
    Menu,
    MenuItem, Paper,
    Tooltip,
    Typography
} from "@mui/material";
import {BaseService} from "../../services/base-service";
import * as Icons from "@mui/icons-material";
import {
    AccountCircle, ArrowForwardIos,
    ExitToApp,
    InfoRounded,
    MenuOpen,
    MoreVertOutlined,
    Notifications,
    PersonOutline
} from "@mui/icons-material";
import {toast} from "react-toastify";
import {ErrorComponent} from "../error/error.component";
import {Link, matchPath, NavLink, useLocation} from "react-router-dom";
import {translate} from "../../translate/translate";
import bgHeader from "../../../assets/images/bg-header.svg";
import {useAuth} from "../../contexts/auth-context";
import {URLS} from "../../services/app-urls";
import {URLS as PAGE_URLS} from "../../services/routes";
import {ItemMenu, ModuleMenu} from "../../models/default/module-menu";
import {Module} from "../../models/default/module";
import ModuleDialogComponent from "../module-dialog/module-dialog.component";
import {AlertModel} from "../../models/default/alert";
import Helpers from "../../../helpers";
import {useNavigate} from "react-router-dom";
import { ReactComponent as ReplenishmentIcon } from "../../../assets/images/replenishment-icon.svg";

interface LayoutProps {
    showCommonElements?: boolean;
    children?: React.ReactNode;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
    }),
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== "open"})(
    ({theme, open}) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...(open && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            "& .MuiDrawer-paper": closedMixin(theme),
        }),
    }),
);

const LayoutComponent: React.FC<LayoutProps> = ({children, showCommonElements = true}) => {
    const {user, logout} = useAuth();
    const [open, setOpen] = React.useState(true);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorElMenu, setAnchorElMenu] = React.useState<null | HTMLElement>(null);
    const [anchorElModules, setAnchorElModules] = React.useState<null | HTMLElement>(null);
    const openUserPanel = Boolean(anchorEl);
    const openModulesPanel = Boolean(anchorElModules);

    const serviceModuleMenu = new BaseService<any>(URLS.MODULE_MENU);
    const serviceModule = new BaseService<Module>(URLS.MODULE);
    const [moduleMenu, setModuleMenu] = React.useState<ModuleMenu[]>([]);
    const [options, setOptions] = React.useState<{ value: string | number; label: string; icon?: string }[]>([]);
    const [openProceed, setOpenProceed] = React.useState(false);

    // const webSocketService = new WebSocketService("alerts");
    // const serviceAlert = new BaseService<AlertModel>(URLS.ALERT);
    const [alerts, setAlerts] = React.useState<AlertModel[]>([]);
    const [countAlerts, setCountAlerts] = React.useState<number | any>(null);
    const [anchorElAlert, setAnchorElAlert] = React.useState<null | HTMLElement>(null);
    const openAlertMenu = Boolean(anchorElAlert);
    const navigate = useNavigate();
    const {pathname: route} = useLocation();

    const exceptionIconsMenu: {route: string, icon: React.ElementType}[] = [
        {
            route: "/replenishment",
            icon: ReplenishmentIcon
        },
    ]

    const serializeValue = (value: any) => {
        return JSON.stringify(value);
    };

    const deserializeValue = (value: any) => {
        if (!value) {
            return "";
        }
        try {
            return JSON.parse(value);
        } catch {
            return "";
        }
    };

    const initialSelectedOption = deserializeValue(localStorage.getItem("module"));
    const [selectedOption, setSelectedOption] = React.useState<string>(
        initialSelectedOption?.module?.module?.label ? initialSelectedOption?.module?.module?.label : "NRE"
    );
    const [icon, setIcon] = React.useState<string>(
        initialSelectedOption?.module?.module?.icon ? initialSelectedOption?.module?.module?.icon : "PostAdd"
    );

    const isNpiModule = selectedOption === "NPI";

    const handleSelectedModule = (module: any) => {
        setSelectedOption(module?.module?.label);
        setIcon(module?.module?.icon || "PostAdd");
        localStorage.setItem("module", serializeValue({module}));
    };

    const clearModuleMenu = () => {
        setModuleMenu([]);
    };

    const handleOpenDialog = () => {
        setOpenProceed(true);
    };

    const handleCloseDialog = () => {
        setOpenProceed(false);
    };

    const handleDrawer = () => {
        setOpen(!open);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClickModules = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElModules(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCloseModules = () => {
        setAnchorElModules(null);
    };

    const moduleHomeRoutes: Record<string, string> = {
        NRE: PAGE_URLS.HOME,
        NPI: PAGE_URLS.NPI_HOME,
        MapLog: PAGE_URLS.MAPLOG_HOME,
    };
    const moduleHomeRoute = moduleHomeRoutes[selectedOption] || PAGE_URLS.HOME;

    const selectModule = (module: { value: string | number; label: string; icon?: string }) => {
        handleSelectedModule({module});
        clearModuleMenu();
        handleCloseModules();
        navigate(moduleHomeRoutes[module.label] || PAGE_URLS.HOME);
        getMenu(module.label);
    };

    const handleClickAlert = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElAlert(event.currentTarget);
    };

    const handleCleanAlert = (alert?: AlertModel) => {
        // if (alert) {
        //     serviceAlert.clearParameters();
        //     serviceAlert.update({"active": false}, alert.id).then(() => {
        //         getAlerts();
        //         setAnchorElAlert(null);
        //         navigate(alert.action);
        //     });
        // } else {
        //     setAnchorElAlert(null);
        // }

    };

    const cleanAlerts = () => {
        setCountAlerts(null);
    };

    const handleCloseAlertsMenu = () => {
        setAnchorElAlert(null);
        cleanAlerts();
    };

    useEffect(() => {
        getModule();
        getMenu();
        getAlerts();
        createWebSocketConnection();
    }, []);

    const getModule = () => {
        serviceModule.clearParameters();
        serviceModule.addParameter("user", user.id);
        serviceModule.addParameter("granted", "true");
        serviceModule.addParameter("is_active", "true");
        serviceModule.getAllFromListRoute("with_granted")
            .then((response: any) => {
                const moduleOptions = response.data
                    .filter((module: Module) => module && module.description !== "MapLog")
                    .map((module: Module) => ({value: module.id, label: module.description, icon: module.icon}));
                setOptions(moduleOptions);
            })
            .catch((error: any) => {
                if (error.response) {
                    toast.error(translate.t("session_expired"));
                } else {
                    toast.error(translate.t("connection_error"));
                }
            });
    };

    const getMenu = (module?: string) => {
        serviceModuleMenu.clearParameters();
        serviceModuleMenu.addParameter("user", user?.id || 1);
        serviceModuleMenu.addParameter("module", module || selectedOption);
        serviceModuleMenu.getAllFromListRoute("find_menu")
            .then((response: any) => {
                const module = response.data.results;
                setModuleMenu(module);
            })
            .catch((error: any) => {
                if (error.response?.status === 403) {
                    logout();
                } else if (error.response?.data?.message) {
                    toast.error(translate.t(error.response.data.message));
                } else {
                    ErrorComponent(error);
                }
            });
    };

    const createWebSocketConnection = () => {
        // webSocketService.createWebSocketConnection();
        // webSocketService.onMessage(() => {
        //     getAlerts();
        // });
    };

    const getAlerts = () => {
        // serviceAlert.clearParameters();
        // serviceAlert.addParameter("active", true);
        // serviceAlert.getAll()
        //     .then((response) => {
        //         // const countAlerts = response.data.length;
        //         // setCountAlerts(countAlerts);
        //         // setAlerts(response.data);
        //     });

    };

    const handleOpenMenu = (item: ItemMenu, event?: React.MouseEvent<HTMLElement>) => {
        item.open = !item.open;
        setModuleMenu([...moduleMenu]);
        if (item.open && event) {
            setAnchorElMenu(event.currentTarget);
        } else {
            setAnchorElMenu(null);
        }
    }

    useEffect(() => {
        if (route === PAGE_URLS.ACCESS_DENIED)
            return;

        const routeHome = matchPath(`${PAGE_URLS.NPI_HOME}/*`, route)
            ? PAGE_URLS.NPI_HOME
            : matchPath(`${PAGE_URLS.MAPLOG_HOME}/*`, route)
                ? PAGE_URLS.MAPLOG_HOME
                : PAGE_URLS.HOME;

        if (routeHome !== moduleHomeRoute)
            navigate(moduleHomeRoute, {replace: true});
    }, [route, moduleHomeRoute, navigate]);

    return (
        <>
            {showCommonElements && (
                <Box sx={{display: "flex"}}>
                    <AppBar sx={{display: "flex", boxShadow: "none"}} open={open}>
                        <Toolbar
                            style={{
                                backgroundImage: `url(${bgHeader})`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "cover",
                                backgroundPosition: "center center",
                            }}>
                            <IconButton
                                id="btn_menu"
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawer}
                                edge="start"
                                sx={{marginRight: 2}}>
                                <MenuOpen sx={{display: open ? "block" : "none", fontSize: 34}}/>
                                <MenuIcon sx={{display: open ? "none" : "block", fontSize: 34}}/>
                            </IconButton>
                            <Box sx={{display: "flex"}}>
                                <Link to={`/${isNpiModule ? "npi" : "home"}`} className="logo">
                                    <Box
                                        component="img"
                                        sx={{
                                            height: isNpiModule ? 24 : 32,
                                        }}
                                        alt={isNpiModule ? "Logo NPI" : "Logo NRE Control"}
                                        src={isNpiModule ? logoNpi : logo}/>
                                </Link>
                            </Box>

                            <Button
                                id="alert-button"
                                color="inherit"
                                sx={{marginRight: 2}}
                                onClick={handleClickAlert}>
                                <Badge color="secondary" badgeContent={countAlerts}><Notifications/></Badge>
                            </Button>
                            <Menu
                                id="alert-menu"
                                aria-labelledby="alert-button"
                                anchorEl={anchorElAlert}
                                open={openAlertMenu}
                                onClose={handleCloseAlertsMenu}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}
                                PaperProps={{
                                    style: {
                                        maxHeight: "540px",
                                        width: "320px",
                                    },
                                }}>
                                <List subheader={<li/>}>
                                    <li>
                                        <ul style={{padding: "0", margin: "0"}}>
                                            <ListSubheader className="list-subheader">{translate.t("notifications")}</ListSubheader>
                                            {alerts.length > 0 && alerts.map((alert, index) => {
                                                return (
                                                    <div key={index}>
                                                        <ListItem style={{flexDirection: "column", alignItems: "flex-start"}}>
                                                            <Typography fontWeight="600" variant="body2" gutterBottom>{Helpers.formatDate(alert.created_at)}</Typography>
                                                            <Typography variant="body2">{alert.name}</Typography>
                                                            <Button startIcon={<InfoRounded sx={{fontSize: "12px !important"}}/>} variant="outlined" onClick={() => {
                                                                handleCleanAlert(alert);
                                                            }}>
                                                                <Typography variant="body2">{translate.t("verify_planning")}</Typography>
                                                            </Button>
                                                        </ListItem>
                                                        <Divider component="li"/>
                                                    </div>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                </List>
                            </Menu>

                            {/*<Button*/}
                            {/*    id="btn_module"*/}
                            {/*    variant="contained"*/}
                            {/*    onClick={() => handleOpenDialog()}*/}
                            {/*    sx={{display: "flex", alignItems: "center", marginRight: "15px"}}>*/}
                            {/*    {React.createElement((Icons as any)[icon], {*/}
                            {/*        sx: {*/}
                            {/*            fontSize: "1.5rem",*/}
                            {/*            marginRight: "0.5rem"*/}
                            {/*        }*/}
                            {/*    })}*/}
                            {/*    <span style={{fontSize: "1rem"}}>{translate.t(selectedOption)}</span>*/}
                            {/*</Button>*/}
                            <Button
                                sx={{
                                border: "1px solid transparent", 
                                marginRight: "15px", 
                                borderRadius: "4px", 
                                backgroundColor: "#2962FF", 
                                "&:hover": {backgroundColor: "rgba(41, 98, 255, 0.8)"
                                }}}
                                id="btn_modules"
                                variant="contained"
                                onClick={handleClickModules}
                                startIcon={<MdViewModule style={{fontSize: "24px"}}/>}>
                                <span style={{
                                    fontSize: "1rem",
                                }}>{translate.t("modules")}</span>
                            </Button>
                             <Menu
                                id="demo-positioned-menu-modules"
                                aria-labelledby="demo-positioned-button-modules"
                                anchorEl={anchorElModules}
                                open={openModulesPanel}
                                onClose={handleCloseModules}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}
                                PaperProps={{
                                    sx: {
                                        borderRadius: "20px",
                                        boxShadow: "0px 2px 5px 0px #0000001F",
                                    },
                                }}>
                                <Box padding={"24px"} className="box-modules" sx={{width: "234px", display: "flex", flexDirection: "column", gap: "10px"}}>
                                    <Grid>
                                         <Typography variant={"subtitle1"} sx={{
                                            marginBottom: "16px",
                                            color: "#505050",
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            lineHeight: "160%",
                                            letterSpacing: "0.15px",
                                         }}>
                                            <span>Selecione um módulo:</span>
                                         </Typography>
                                        <Grid display="flex" flexDirection="column" sx={{gap: "12px"}}>
                                            {options.length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{maxWidth: "186px"}}>
                                                    {translate.t("no_modules_available")}
                                                </Typography>
                                            )}
                                            {options.map((module) => {
                                                const isSelected = module.label === selectedOption;
                                                return (
                                                    <Button
                                                        key={module.value}
                                                        sx={{
                                                            width: "186px",
                                                            height: "80px",
                                                            border: isSelected ? "1px solid transparent" : "1px solid #E2E8F0",
                                                            backgroundColor: isSelected ? "#295FFF" : "#FFFFFF",
                                                            color: isSelected ? "#FFFFFF" : "#606369",
                                                            fontWeight: 500,
                                                            borderRadius: "8px",
                                                            textTransform: "none",
                                                            boxShadow: "none",
                                                            "&:hover": {
                                                                backgroundColor: "#507CFF",
                                                                color: "#FFFFFF",
                                                                boxShadow: "none",
                                                            },
                                                        }}
                                                        onClick={() => selectModule(module)}>
                                                        <strong style={{fontSize: 20}}>{module.label}</strong>
                                                    </Button>
                                                );
                                            })}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Menu>

                            <Button
                                id="btn_profile"
                                variant="contained"
                                onClick={handleClick}
                                startIcon={<PersonOutline sx={{fontSize: "24px !important"}}/>}>
                                <span style={{
                                    fontSize: "1rem",
                                    textTransform: "uppercase"
                                }}>{String(user?.username)}</span>
                            </Button>
                            <Menu
                                id="demo-positioned-menu"
                                aria-labelledby="demo-positioned-button"
                                anchorEl={anchorEl}
                                open={openUserPanel}
                                onClose={handleClose}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}>
                                <Box padding={2} className="box-user">
                                    <Grid spacing={2} container direction={"row"}>
                                        <Grid>
                                            <AccountCircle sx={{fontSize: 40, marginTop: 1}}/>
                                        </Grid>
                                        <Grid>
                                            <Typography variant={"subtitle1"}>
                                                <strong style={{fontSize: 20}}>{String(user?.username)}</strong>
                                            </Typography>
                                            <Typography variant={"subtitle2"}>
                                                <span style={{fontSize: 14}}>{String(user?.name)}</span>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider></Divider>
                                <MenuItem style={{marginTop: "8px"}} onClick={() => logout()}>
                                    <ListItemIcon id="btn_logout">
                                        <ExitToApp/>
                                    </ListItemIcon>
                                    <ListItemText>{translate.t("logout")}</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Toolbar>
                    </AppBar>
                    <Drawer
                        variant="permanent" open={open} className="sidenav"
                        PaperProps={{
                            style: {
                                width: open ? "140px" : "65px",
                                marginTop: "64px",
                                height: "calc(100vh - 64px)"
                            }
                        }}>
                        <Divider/>
                        <List component={"nav"}>
                            {moduleMenu.map((menu: any) => (
                                <Tooltip key={menu.label} title={open ? '' : translate.t(menu.label)} placement={"top-end"}>
                                    <ListItem key={menu.label}>
                                        {!menu.items ? (
                                            !menu.hidden && (
                                                <ListItemButton
                                                    id={`btn_${menu.label}`}
                                                    sx={{
                                                        minHeight: open ? 36 : 0,
                                                        justifyContent: open ? "initial" : "center",
                                                    }}
                                                    component={NavLink}
                                                    to={menu.route}>
                                                    <Grid container spacing={2}>
                                                        <Grid size={isNpiModule ? 12 : (open ? 10 : 12)}>
                                                            <ListItemIcon
                                                                sx={{
                                                                    minWidth: 0,
                                                                    mr: open ? 2 : "auto",
                                                                    justifyContent: "center"
                                                                }}>
                                                                {
                                                                    (() => {
                                                                        const exception = exceptionIconsMenu.find(it => it.route === menu.route);
                                                                        if (exception) {
                                                                            const Icon = exception.icon;
                                                                            return <div style={{marginLeft: '-4px'}}><Icon /></div>;
                                                                        }

                                                                        return React.createElement((Icons as any)[menu.icon]);
                                                                    })()
                                                                }
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                key={menu.label}
                                                                sx={{display: open ? "initial" : "none"}}
                                                                primary={translate.t(menu.label).length > 50 ?
                                                                    `${translate.t(menu.label).substring(0, 50)}...` :
                                                                    translate.t(menu.label)}
                                                            />
                                                        </Grid>
                                                        {open && !isNpiModule && <Grid size={2}></Grid>}
                                                    </Grid>
                                                </ListItemButton>
                                            )
                                        ) : (
                                            !menu.hidden && (
                                                <>
                                                    <ListItemButton
                                                            component={"a"}
                                                        id={`btn_${menu.label}`}
                                                        aria-controls={`lock-menu-${menu.label}`}
                                                        aria-haspopup="listbox"
                                                        aria-expanded={menu.open ? 'true' : undefined}
                                                        sx={{
                                                            position: isNpiModule ? "relative" : "static",
                                                            minHeight: open ? 36 : 0,
                                                            justifyContent: open ? "initial" : "center",
                                                            ...(route.includes(menu.route) && {
                                                                color: "#FFFFFF !important",
                                                                "&:hover": {
                                                                    bgcolor: "#2962FF !important",
                                                                },
                                                            }),
                                                            "&.Mui-selected": {
                                                                bgcolor: "#2962FF",
                                                                color: "#FFFFFF !important",
                                                            },
                                                            "&.Mui-selected:hover": {
                                                                bgcolor: "unset !important",
                                                            },
                                                        }}
                                                        onClick={event => handleOpenMenu(menu, event)}
                                                        selected={route.includes(menu.route)}
                                                    >
                                                        <Grid container spacing={2}>
                                                            <Grid size={isNpiModule ? 12 : 10}>
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        mr: open ? 2 : "auto",
                                                                        justifyContent: "center",
                                                                        ...(route.includes(menu.route) && {
                                                                            color: "#FFFFFF !important",
                                                                            "&:hover": {
                                                                                color: "#FFFFFF !important",
                                                                                pointerEvents: "none",
                                                                            }
                                                                        })
                                                                    }}>
                                                                    {React.createElement((Icons as any)[menu.icon])}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    key={menu.label}
                                                                    sx={{display: open ? "initial" : "none"}}
                                                                    primary={translate.t(menu.label).length > 50 ?
                                                                        `${translate.t(menu.label).substring(0, 50)}...` :
                                                                        translate.t(menu.label)}
                                                                    primaryTypographyProps={{
                                                                        sx: {
                                                                            ...(route.includes(menu.route) && {
                                                                                color: "#FFFFFF !important",
                                                                                "&:hover": {
                                                                                    color: "#FFFFFF !important",
                                                                                    pointerEvents: "none",
                                                                                }
                                                                            })
                                                                        }
                                                                    }}
                                                                />
                                                            </Grid>
                                                            {!isNpiModule && (
                                                            <Grid size={2}
                                                                  display="flex"
                                                                  alignItems="center"
                                                                  justifyContent="flex-end"
                                                                  sx={{
                                                                      paddingLeft: "0px !important"
                                                                  }}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        ...(route.includes(menu.route) && {
                                                                            color: "#FFFFFF !important",
                                                                            "&:hover": {
                                                                                color: "#2962FF !important",
                                                                            }
                                                                        }),
                                                                        display: open ? "initial" : "none",
                                                                        minWidth: 0,
                                                                        width: "1rem !important",
                                                                        height: "1rem !important"
                                                                    }}
                                                                >
                                                                    <ArrowForwardIos
                                                                        fontSize={"small"}
                                                                        sx={{
                                                                            width: "1rem !important",
                                                                            height: "1rem !important"
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                            </Grid>
                                                            )}
                                                        </Grid>
                                                            {isNpiModule && open && (
                                                                <ListItemIcon
                                                                    sx={{
                                                                        position: "absolute",
                                                                        right: 8,
                                                                        top: "50%",
                                                                        transform: "translateY(-50%)",
                                                                        minWidth: 0,
                                                                        width: "1rem !important",
                                                                        height: "1rem !important",
                                                                        ...(route.includes(menu.route) && {
                                                                            color: "#FFFFFF !important",
                                                                            "&:hover": {
                                                                                color: "#2962FF !important",
                                                                            }
                                                                        }),
                                                                    }}
                                                                >
                                                                    <ArrowForwardIos
                                                                        fontSize={"small"}
                                                                        sx={{
                                                                            width: "1rem !important",
                                                                            height: "1rem !important"
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                            )}
                                                    </ListItemButton>
                                                    <Menu
                                                        id={`lock-menu-${menu.label}`}
                                                        anchorEl={anchorElMenu}
                                                        open={menu.open}
                                                        onClose={event => handleOpenMenu(menu)}
                                                        MenuListProps={{
                                                            'aria-labelledby': `btn_${menu.label}`,
                                                            role: 'listbox',
                                                        }}
                                                        anchorOrigin={{
                                                            vertical: "top",
                                                            horizontal: "right",
                                                        }}
                                                        transformOrigin={{
                                                            vertical: "top",
                                                            horizontal: "left",
                                                        }}
                                                    >
                                                        {menu.items.map((child: any) => (
                                                            !child.hidden && (
                                                                <MenuItem
                                                                        onClick={() => {
                                                                        handleClose();
                                                                        handleOpenMenu(menu);
                                                                    }}
                                                                    component={NavLink}
                                                                    to={child.route}
                                                                >
                                                                    {React.createElement((Icons as any)[child.icon])}&ensp;
                                                                    {translate.t(child.label)}
                                                                </MenuItem>
                                                            )
                                                        ))}
                                                    </Menu>
                                                </>
                                            )
                                        )}
                                    </ListItem>
                                </Tooltip>
                            ))}
                        </List>
                    </Drawer>
                    <Box component="main" sx={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "calc(100vh - 64px)",
                        marginTop: "64px"
                    }}>
                        <Toolbar className="subheader"/>
                        <Container
                            maxWidth={false}
                            disableGutters
                            sx={{
                                flexGrow: 1,
                                p: 3,
                                padding: 3,
                            }}
                            className="main-container"
                        >
                            <main style={{height: "100%"}}>{children}</main>
                        </Container>
                        <Paper sx={{borderRadius: "0"}}>
                            <Grid padding="10px 16px" container direction="row" justifyContent="space-between" alignItems="center">
                                <Grid display="flex" flexDirection="row" alignItems="center">
                                    <Box
                                        marginRight={1}
                                        component="img"
                                        sx={{
                                            width: "auto",
                                            height: 30,
                                        }}
                                        alt="Logo Foxconn"
                                        src={logoFoxconn}/>
                                </Grid>
                                <Grid display="flex" flexDirection="row" alignItems="center">
                                    <Typography fontSize="12px" color="grey">
                                        2026 Foxconn. All rights reserved | Developed by FPFtech
                                    </Typography>
                                </Grid>
                                <Grid display="flex" flexDirection="row" alignItems="center">
                                    <Typography fontSize="12px" color="grey">
                                        Version: 0.0
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                </Box>
            )}
            <ModuleDialogComponent
                openProceed={openProceed}
                handleCloseDialogProceed={handleCloseDialog}
                modules={options}
                handleSelectedModule={handleSelectedModule}
                handleClearModuleMenu={clearModuleMenu}
                searchMenus={getMenu}/>
        </>
    );

};

export default LayoutComponent;
